import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/enhanced-button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  createVirtualPlEntrySchema,
  SECTION_KEY_OPTIONS,
  type CreateVirtualPlEntryFormValues,
} from "@/schemas/virtualPlEntry.schema";

interface CreateVirtualEntryFormProps {
  onSubmit: (values: CreateVirtualPlEntryFormValues) => Promise<void>;
  onCancel: () => void;
  // Present only when adding from a multi-company View — lets the user pick which
  // company in the view the entry belongs to. Absent when a single company is active.
  companies?: { id: string; name: string }[];
}

export function CreateVirtualEntryForm({ onSubmit, onCancel, companies }: CreateVirtualEntryFormProps) {
  const form = useForm<CreateVirtualPlEntryFormValues>({
    resolver: zodResolver(createVirtualPlEntrySchema(!!companies?.length)),
    defaultValues: {
      sectionKey: "income",
      lineName: "",
      amount: 0,
      effectiveMonth: new Date().toISOString().slice(0, 7),
      description: "",
      organizationId: "",
    },
  });

  const handleSubmit = async (values: CreateVirtualPlEntryFormValues) => {
    await onSubmit(values);
    form.reset();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-3">
        {companies && companies.length > 0 && (
          <FormField
            control={form.control}
            name="organizationId"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Company</FormLabel>
                <Select value={field.value} onValueChange={field.onChange}>
                  <FormControl>
                    <SelectTrigger className="h-8 text-sm">
                      <SelectValue placeholder="Select a company" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <FormField
          control={form.control}
          name="sectionKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">P&L section</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {SECTION_KEY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="lineName"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Line name</FormLabel>
              <FormControl>
                <Input placeholder="e.g. Expected trader revenue" className="h-8 text-sm" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-2 gap-3">
          <FormField
            control={form.control}
            name="amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Amount</FormLabel>
                <FormControl>
                  <Input type="number" step="0.01" placeholder="0.00" className="h-8 text-sm" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="effectiveMonth"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-xs">Month</FormLabel>
                <FormControl>
                  <Input type="month" className="h-8 text-sm" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-xs">Description (optional)</FormLabel>
              <FormControl>
                <Textarea placeholder="Why this adjustment?" className="min-h-[60px] text-sm resize-none" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={form.formState.isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? "Saving…" : "Add entry"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
